import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { MapPin, Phone, Mail, Clock3, Loader2, Send, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We will get back to you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        label="Contact Us"
        title="Let's Talk About Your Project"
        subtitle="Reach us by WhatsApp, email or the form below — our KL team responds within one business day."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="glow-card p-6" data-testid="contact-address-card">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-cyan-400 shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-white font-semibold">Headquarters</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                    Suite 33-01, 33rd Floor, Menara Keck Seng,<br />
                    203 Jalan Bukit Bintang, 55100 Kuala Lumpur, Malaysia
                  </p>
                  <span className="spec-chip inline-block mt-3">REG. NO. 201903139239</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <a
                href="https://wa.me/601172283321"
                target="_blank"
                rel="noopener noreferrer"
                className="glow-card p-6 block group"
                data-testid="contact-whatsapp-card"
              >
                <Phone className="w-6 h-6 text-[#25D366] mb-3" />
                <h3 className="font-display text-white font-semibold text-sm">WhatsApp / Call</h3>
                <p className="text-sm text-slate-400 mt-1 group-hover:text-cyan-400 transition-colors">011-7228 3321</p>
              </a>
              <a href="mailto:ycsystem.sales@gmail.com" className="glow-card p-6 block group" data-testid="contact-email-card">
                <Mail className="w-6 h-6 text-cyan-400 mb-3" />
                <h3 className="font-display text-white font-semibold text-sm">Email</h3>
                <p className="text-sm text-slate-400 mt-1 group-hover:text-cyan-400 transition-colors break-all">ycsystem.sales@gmail.com</p>
              </a>
            </div>

            <div className="glow-card p-6" data-testid="contact-hours-card">
              <div className="flex items-start gap-4">
                <Clock3 className="w-6 h-6 text-cyan-400 shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-white font-semibold">Operating Hours</h3>
                  <p className="text-sm text-slate-400 mt-2">Monday – Friday: 9:00 AM – 6:00 PM</p>
                  <p className="text-sm text-slate-400">Saturday: 9:00 AM – 1:00 PM (by appointment)</p>
                </div>
              </div>
            </div>

            <div className="glow-card overflow-hidden" data-testid="contact-map">
              <iframe
                title="YC SYSTEM HQ - Menara Keck Seng"
                src="https://maps.google.com/maps?q=Menara%20Keck%20Seng%2C%20Jalan%20Bukit%20Bintang%2C%20Kuala%20Lumpur&z=16&output=embed"
                className="w-full h-64 border-0 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </div>

          <div className="glow-card p-8 h-fit" data-testid="contact-form-card">
            {sent ? (
              <div className="text-center py-10" data-testid="contact-success">
                <Send className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="font-display text-2xl font-bold text-white">Message Sent</h3>
                <p className="text-slate-400 mt-3 text-sm">Thank you — we will reply within one business day.</p>
                <a
                  href="https://wa.me/601172283321"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 border border-[#25D366]/60 text-white text-sm font-semibold hover:bg-[#25D366]/10 transition-colors"
                  data-testid="contact-success-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> Chat Now on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <h3 className="font-display text-xl font-semibold text-white mb-2">Send Us a Message</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Name *</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-form-name-input"
                      className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Email *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-form-email-input"
                      className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Phone</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="contact-form-phone-input"
                      className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Subject *</label>
                    <input
                      required
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      data-testid="contact-form-subject-input"
                      className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="tech-label !text-[0.6rem] block mb-2">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-form-message-input"
                    className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-button"
                  className="w-full px-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold transition-colors border border-cyan-400/40 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Message <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
